/**
 * Core Dots & Boxes Game Types
 *
 * All TypeScript types and interfaces for the Dots & Boxes game.
 * Grid of dots with lines between them. Players take turns drawing lines.
 * Completing the 4th side of a box claims it and grants an extra turn.
 * Player with the most boxes wins.
 */

// ============================================================================
// Board Configuration
// ============================================================================

/** Player color identifier */
export type PlayerColor = 'blue' | 'red' | 'green' | 'orange';

/** All player colors in order */
export const PLAYER_COLORS: PlayerColor[] = ['blue', 'red', 'green', 'orange'];

/** Direction of a line segment */
export type LineDirection = 'horizontal' | 'vertical';

/** A line between two adjacent dots */
export interface Line {
  row: number;
  col: number;
  direction: LineDirection;
}

/** A box on the grid */
export interface Box {
  row: number;
  col: number;
  owner: PlayerColor | null;
}

/** The game board representation */
export interface DotsBoxesBoard {
  /** horizontalLines[row][col] — true if drawn. Rows: 0..gridSize, Cols: 0..gridSize-2 */
  horizontalLines: boolean[][];
  /** verticalLines[row][col] — true if drawn. Rows: 0..gridSize-1, Cols: 0..gridSize */
  verticalLines: boolean[][];
  /** boxes[row][col] — owner of each box. Rows: 0..gridSize-1, Cols: 0..gridSize-1 */
  boxes: (PlayerColor | null)[][];
  /** Who drew each line — keyed by "row,col,direction" */
  lineOwners: Record<string, PlayerColor>;
}

/** Grid size options (dots per side) */
export type GridSize = 3 | 4 | 5 | 6;

/** Available grid sizes */
export const GRID_SIZES: { dots: GridSize; boxes: number; label: string }[] = [
  { dots: 3, boxes: 4, label: '3×3 (4 boxes)' },
  { dots: 4, boxes: 9, label: '4×4 (9 boxes)' },
  { dots: 5, boxes: 16, label: '5×5 (16 boxes)' },
  { dots: 6, boxes: 25, label: '6×6 (25 boxes)' },
];

// ============================================================================
// Game State
// ============================================================================

/** A move in the game (drawing a line) */
export interface DotsBoxesMove {
  player: PlayerColor;
  line: Line;
  boxesCompleted: number;
  timestamp: number;
}

export interface DotsBoxesGameState {
  board: DotsBoxesBoard;
  gridSize: GridSize;
  currentPlayerIndex: number;
  players: PlayerColor[];
  moveHistory: DotsBoxesMove[];
  gameStatus: 'active' | 'completed';
  winner?: PlayerColor | 'draw';
  scores: Record<PlayerColor, number>;
  gameId: string;
  startedAt: number;
  totalLines: number;
  drawnLines: number;
}

// ============================================================================
// Game Settings
// ============================================================================

export type DotsBoxesDifficulty = 'easy' | 'medium' | 'hard';

export interface DotsBoxesGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  gridSize: GridSize;
  playerCount: number;
  aiDifficulty?: DotsBoxesDifficulty;
}

// ============================================================================
// Statistics
// ============================================================================

export interface DotsBoxesGameStats {
  wins: number;
  losses: number;
  draws: number;
  winPercentage: number;
  averageScore: number;
  highestScore: number;
  totalBoxesClaimed: number;
}

export interface DotsBoxesStatistics {
  userId?: string;
  singlePlayer: {
    easy: DotsBoxesGameStats;
    medium: DotsBoxesGameStats;
    hard: DotsBoxesGameStats;
  };
  localMultiplayer: DotsBoxesGameStats;
  onlineMultiplayer: DotsBoxesGameStats;
  totalGames: number;
  totalPlayTime: number;
}

export interface DotsBoxesGameResult {
  gameId: string;
  mode: DotsBoxesGameSettings['mode'];
  difficulty?: DotsBoxesDifficulty;
  gridSize: GridSize;
  playerCount: number;
  winner?: PlayerColor | 'draw';
  scores: Record<PlayerColor, number>;
  totalMoves: number;
  duration: number;
  completedAt: number;
}

// ============================================================================
// Component Props
// ============================================================================

export interface DotsBoxesGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

export interface DotsBoxesGameModeSelectorProps {
  onStartGame: (settings: DotsBoxesGameSettings) => void;
  onJoinGame?: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
  isOnline?: boolean;
}

export interface DotsBoxesBoardProps {
  board: DotsBoxesBoard;
  gridSize: GridSize;
  currentPlayer: PlayerColor;
  onLineClick: (line: Line) => void;
  disabled?: boolean;
}
